/**
 * Session Slice
 *
 * Manages session initialization state, separate from authentication.
 * Tracks whether the backend session is ready and any init errors.
 */
import { createSlice } from '@reduxjs/toolkit';
import { initSessionThunk, createSessionThunk } from '../thunks/sessionThunks';
import { logoutThunk } from '../thunks/authThunks';

// ============================================
// State Types
// ============================================

export interface SessionState {
  readonly isReady: boolean;
  readonly isInitializing: boolean;
  readonly error: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialSessionState: SessionState = {
  isReady: false,
  isInitializing: false,
  error: null,
};

// ============================================
// Slice
// ============================================

const sessionSlice = createSlice({
  name: 'session',
  initialState: initialSessionState,
  reducers: {
    resetSessionState: () => initialSessionState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(initSessionThunk.pending, (state) => {
        state.isInitializing = true;
        state.error = null;
      })
      .addCase(initSessionThunk.fulfilled, (state) => {
        state.isReady = true;
        state.isInitializing = false;
        state.error = null;
      })
      .addCase(initSessionThunk.rejected, (state, action) => {
        state.isReady = true;
        state.isInitializing = false;
        state.error = action.payload ?? 'Session restore failed';
      });

    builder
      .addCase(createSessionThunk.pending, (state) => {
        state.isInitializing = true;
        state.error = null;
      })
      .addCase(createSessionThunk.fulfilled, (state) => {
        state.isReady = true;
        state.isInitializing = false;
        state.error = null;
      })
      .addCase(createSessionThunk.rejected, (state, action) => {
        state.isInitializing = false;
        state.error = action.payload ?? 'Session creation failed';
      });

    builder.addCase(logoutThunk.fulfilled, () => initialSessionState);
  },
});

// ============================================
// Actions & Reducer
// ============================================

export const { resetSessionState } = sessionSlice.actions;
export const sessionReducer = sessionSlice.reducer;
